import { useEffect, useMemo, useState } from "react";
import { useConfigure, useHits, useInstantSearch, useSearchBox, useSortBy } from "react-instantsearch";

import { ALGOLIA_RETRIEVED_ATTRIBUTES, getAlgoliaIndex, getAlgoliaReplica } from "@repo/shopifyV2/algolia/client";
import { ALGOLIA_FACET, VISIBLE_PRODUCTS_FILTER } from "@repo/shopifyV2/algolia/config";
import { toFilterValues } from "@repo/shopifyV2/algolia/facets";
import { markAlgoliaDown } from "@repo/shopifyV2/algolia/status";
import useSyncedFacet from "@repo/shopifyV2/algolia/useSyncedFacet";
import type { IAlgoliaProductHit } from "@repo/shopifyV2/interface/algolia";
import type { ISearchQuery } from "@repo/shopifyV2/interface/search";
import type { Filter } from "@repo/shopifyV2/types/storefront.types";

import { toProductCards, toProductGroups } from "@hooks/algolia/groupHits";

/**
 * 한 번에 받아 오는 변형(variant) 레코드 수.
 *
 * 인덱스가 변형 단위인데 화면은 상품·색상 단위라, 나눠 받으면 한 상품의 색상이 페이지 경계에서 잘린다
 * (실측: 60건씩 받으면 상품 20개 중 17개의 색상칩이 비었다). 그래서 매칭된 변형을 한 번에 받아
 * 상품으로 묶은 뒤 페이지네이션은 화면에서 한다. 응답은 gzip 기준 50KB 안팎이다.
 *
 * Algolia 는 기본적으로 1000번째 결과까지만 조회할 수 있어(paginationLimitedTo) 이 값이 상한이다.
 */
const MAX_HITS = 1000;

/**
 * Load More 한 번에 늘어나는 상품 수.
 * 기존 Shopify 쿼리의 first 값과 동일하게 맞춘다(view === "seperate" 일 때만 12, 그 외 24).
 */
const PRODUCTS_PER_PAGE = { seperate: 12, default: 24 };

/** Shopify 색상 필터는 값 id 에 "group" 이 들어간 것만 노출한다(Filter/index.tsx). */
const COLOR_VALUE_ID = "filter.p.m.group.color";

type TSortName = "relevance" | "price_descending" | "price_ascending";

const PRICE_REPLICA: Record<Exclude<TSortName, "relevance">, "priceDesc" | "priceAsc"> = {
  price_descending: "priceDesc",
  price_ascending: "priceAsc",
};

/** 상품 가격(Shopify 검색의 PRICE 정렬과 같게 상품 최저가 기준) */
const productPrice = (variants: IAlgoliaProductHit[]) => variants[0].variants_min_price ?? variants[0].price ?? 0;

/**
 * 검색 결과 페이지용 Algolia 훅.
 *
 * 기존 SearchResult 의 useQuery 와 같은 모양({ searchData, products, expandProducts, ... })을 돌려주므로
 * 화면 코드는 데이터 출처만 바꾸면 된다.
 */
export default function useAlgoliaSearchResult(queryParameter: string, queryString: Record<string, unknown>) {
  const { refine: refineQuery } = useSearchBox();
  const { status, results, error } = useInstantSearch();
  const { items } = useHits();

  // 카드 표시(products / expandProducts)는 화면이 고르고, 여기서는 페이지 크기만 본다.
  // 기존 화면 그대로 view === "seperate" 일 때만 12개씩이다.
  const pageSize = queryString.view === "seperate" ? PRODUCTS_PER_PAGE.seperate : PRODUCTS_PER_PAGE.default;
  const sort = (typeof queryString.sort === "string" ? queryString.sort : "relevance") as TSortName;

  const [visibleCount, setVisibleCount] = useState(pageSize);

  // 정렬은 replica 인덱스 전환으로 처리한다. replica 가 없으면(설정이 비어 기본 인덱스가 돌아오면)
  // 기본 인덱스로 받은 결과를 화면에서 가격순으로 정렬한다. 매칭된 변형을 한 번에 받으므로 결과가 1000건 이하면 순서가 정확하다.
  const priceReplica = sort === "relevance" ? undefined : PRICE_REPLICA[sort];
  const sortOnClient = priceReplica !== undefined && getAlgoliaReplica(priceReplica) === getAlgoliaIndex();

  const sortIndexes = useMemo(
    () => [
      { label: "relevance", value: getAlgoliaIndex() },
      { label: "price_descending", value: getAlgoliaReplica("priceDesc") },
      { label: "price_ascending", value: getAlgoliaReplica("priceAsc") },
    ],
    [],
  );

  const { refine: refineSort } = useSortBy({ items: sortIndexes });

  // 인덱스 기본값(hitsPerPage 20)으로 받으면 상품이 10개 안팎에서 끝나고 색상칩이 잘린다.
  // 매칭된 변형을 한 번에 받아 화면에서 묶는 전제라 반드시 MAX_HITS 로 요청한다.
  useConfigure({
    hitsPerPage: MAX_HITS,
    filters: VISIBLE_PRODUCTS_FILTER,
    attributesToRetrieve: ALGOLIA_RETRIEVED_ATTRIBUTES,
    attributesToHighlight: [],
    attributesToSnippet: [],
  });

  const parseParam = (key: string) => {
    const raw = queryString[key];
    if (typeof raw !== "string") return [];
    try {
      return JSON.parse(raw || "[]") as string[];
    } catch {
      return [];
    }
  };

  const selectedCategory = parseParam("category");
  const selectedColor = parseParam("color");
  const selectedSize = parseParam("size");

  // 선택된 필터를 문자열로 눌러 둬서 effect 의존성을 안정적으로 만든다.
  const selectedKey = [selectedCategory, selectedColor, selectedSize].map((values) => values.join(",")).join("|");

  const categoryItems = useSyncedFacet(ALGOLIA_FACET.productType, selectedCategory);
  const colorItems = useSyncedFacet(ALGOLIA_FACET.color, selectedColor);
  const sizeItems = useSyncedFacet(ALGOLIA_FACET.size, selectedSize);

  useEffect(() => {
    refineQuery(queryParameter);
  }, [queryParameter]);

  useEffect(() => {
    refineSort(sortIndexes.find((item) => item.label === sort)?.value ?? getAlgoliaIndex());
  }, [sort]);

  // 검색어 · 필터 · 정렬 · 보기 방식이 바뀌면 다시 첫 페이지부터
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [queryParameter, sort, pageSize, selectedKey]);

  useEffect(() => {
    // 인덱스가 사라지는 등 요청이 실패하면 화면을 기존 Shopify 검색으로 되돌린다.
    if (error) markAlgoliaDown(error.message);
  }, [error]);

  // Gift / GWP 는 서버에서 걸러진다(VISIBLE_PRODUCTS_FILTER). hidden 은 toProductGroups 가 거른다.
  const productGroups = useMemo(() => {
    const groups = toProductGroups(items);
    if (!sortOnClient) return groups;

    const direction = sort === "price_ascending" ? 1 : -1;
    // 가격이 같으면 관련도 순서를 유지한다(Array.prototype.sort 는 안정 정렬).
    return [...groups].sort((a, b) => (productPrice(a) - productPrice(b)) * direction);
  }, [items, sortOnClient, sort]);

  const { products, expandProducts } = useMemo(() => toProductCards(productGroups.slice(0, visibleCount)), [productGroups, visibleCount]);

  const hasNextPage = visibleCount < productGroups.length;

  const filters = useMemo<Filter[]>(
    () =>
      [
        {
          id: "filter.p.product_type",
          label: "Category",
          type: "LIST",
          values: toFilterValues("filter.p.product_type", categoryItems),
        },
        {
          id: COLOR_VALUE_ID,
          label: "Color",
          type: "LIST",
          values: toFilterValues(COLOR_VALUE_ID, colorItems),
        },
        {
          id: "filter.v.option.size",
          label: "Size",
          type: "LIST",
          values: toFilterValues("filter.v.option.size", sizeItems),
        },
        {
          // getTotalProductCount 가 id 에 "hidden" 이 든 필터의 No 개수를 전체 상품 수로 쓴다.
          id: "filter.p.m.operation.hidden",
          label: "Hidden",
          type: "BOOLEAN",
          values: [{ id: "filter.p.m.operation.hidden.false", label: "No", count: productGroups.length, input: "{}" }],
        },
      ] as Filter[],
    [categoryItems, colorItems, sizeItems, productGroups.length],
  );

  // 첫 응답 전에는 InstantSearch 가 빈 결과(__isArtificial)를 내보내므로 로딩으로 취급한다.
  const hasResults = Boolean(results) && !(results as unknown as { __isArtificial?: boolean }).__isArtificial;

  const searchData = useMemo(
    () =>
      hasResults
        ? ({
            totalCount: productGroups.length,
            productFilters: filters,
            pageInfo: { hasNextPage, endCursor: "" },
          } as unknown as ISearchQuery["search"])
        : undefined,
    [hasResults, productGroups.length, filters, hasNextPage],
  );

  return {
    searchData,
    products,
    expandProducts,
    hasNextPage,
    loading: status === "loading" || status === "stalled",
    fetchMore: () => {
      setVisibleCount((prev) => prev + pageSize);
    },
  };
}
